import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import Loading from '../../common/Loading';

interface Export {
  fileName: string;
  key: string;
  size: number;
  sizeFormatted: string;
  lastModified: string;
  displayDate: string;
  timestamp: string;
  downloadUrl: string;
}

type ExportSection = 'patient-charts' | 'consents' | 'condition-reports';

const allSections: { value: ExportSection; label: string; description: string }[] = [
  {
    value: 'patient-charts',
    label: 'Patient Charts',
    description: 'Encounters, SOAP notes, chart entries, and test result files',
  },
  {
    value: 'consents',
    label: 'Acknowledgements & Consents',
    description: 'Signed consent PDFs for each patient',
  },
  {
    value: 'condition-reports',
    label: 'Condition & Exercise Reports',
    description: 'Treatment plans, exercises, and care goals as text files',
  },
];

const ClinicExports: React.FC = () => {
  const { clinicID } = useParams<{ clinicID: string }>();
  const { createToast } = useContext(ToastContext);
  const [exports, setExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastGeneratedTime, setLastGeneratedTime] = useState<string | null>(null);
  const [format, setFormat] = useState<'txt' | 'pdf'>('txt');
  const [sections, setSections] = useState<ExportSection[]>([
    'patient-charts',
    'consents',
    'condition-reports',
  ]);

  const loadExports = async () => {
    setLoading(true);

    try {
      const response = await ChiroUpAPI.get(
        'exportApi',
        `/clinics/${clinicID}/export/patient-charts`,
        {}
      );

      if (response && response.exports) {
        setExports(response.exports);

        if (response.exports.length > 0) {
          setLastGeneratedTime(response.exports[0].displayDate);
        }
      }
    } catch (error) {
      createToast({
        title: 'Error',
        description: 'Failed to load exports',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clinicID) {
      loadExports();
    }
  }, [clinicID]);

  const pollExportStatus = async (jobId: string) => {
    const maxAttempts = 180; // 15 minutes max (5 second intervals)
    let attempts = 0;

    const poll = async () => {
      try {
        const status = await ChiroUpAPI.get(
          'exportApi',
          `/clinics/${clinicID}/export/status/${jobId}`,
          {}
        );

        if (status.status === 'completed') {
          createToast({
            title: 'Export Complete!',
            description: `Export generated successfully! ${status.patientCount} patients processed. Refreshing list...`,
            type: ToastTypes.Success,
            duration: 7000,
          });
          await loadExports();
          setGenerating(false);

          return;
        } else if (status.status === 'failed') {
          createToast({
            title: 'Export Failed',
            description: status.error || 'Export generation failed. Please try again.',
            type: ToastTypes.Fail,
            duration: 10000,
          });
          setGenerating(false);

          return;
        }

        // Still processing, poll again
        attempts++;

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          createToast({
            title: 'Export Timeout',
            description:
              'Export is taking longer than expected. Please refresh the page later to check status.',
            type: ToastTypes.Fail,
            duration: 10000,
          });
          setGenerating(false);
        }
      } catch (error) {
        // Continue polling on error (network issues, etc)
        attempts++;

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setGenerating(false);
        }
      }
    };

    poll();
  };

  const toggleSection = (section: ExportSection) => {
    setSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const handleGenerateExport = async () => {
    if (generating) return;

    if (sections.length === 0) {
      createToast({
        title: 'No Sections Selected',
        description: 'Please select at least one data section to export.',
        type: ToastTypes.Fail,
        duration: 5000,
      });

      return;
    }

    const pdfNote =
      format === 'pdf' && sections.includes('patient-charts')
        ? ' Signed encounters will be exported as styled PDFs; unsigned encounters will remain as text files.'
        : '';
    const confirmed = window.confirm(
      'This will generate a new patient data export for all patients in this clinic. ' +
        'The process may take several minutes depending on the number of patients.' +
        pdfNote +
        ' Do you want to continue?'
    );

    if (!confirmed) return;

    setGenerating(true);
    createToast({
      title: 'Generating Export',
      description: 'Starting export generation. This may take a few minutes...',
      type: ToastTypes.Success,
      duration: 10000,
    });

    try {
      const response = await ChiroUpAPI.post(
        'exportApi',
        `/clinics/${clinicID}/export/patient-charts`,
        {
          body: { format, sections },
        }
      );

      // Handle async job response (202 Accepted with jobId)
      if (response && response.jobId) {
        createToast({
          title: 'Export Started',
          description:
            response.message ||
            'Export is being generated. This may take up to 15 minutes. Please check back later.',
          type: ToastTypes.Success,
          duration: 15000,
        });

        // Start polling for job completion
        pollExportStatus(response.jobId);
      } else if (response && response.success) {
        // Legacy success response (for backward compatibility)
        createToast({
          title: 'Success!',
          description: `Export generated successfully! ${response.patientCount} patients processed.`,
          type: ToastTypes.Success,
          duration: 7000,
        });

        // Reload the exports list to show the new export
        await loadExports();
        setGenerating(false);
      } else {
        createToast({
          title: 'Error',
          description: 'Export generation failed. Please try again.',
          type: ToastTypes.Fail,
          duration: 5000,
        });
        setGenerating(false);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to generate export';
      createToast({
        title: 'Error',
        description: errorMessage,
        type: ToastTypes.Fail,
        duration: 5000,
      });
      setGenerating(false);
    }
  };

  const handleDownload = (exportItem: Export) => {
    // Open the pre-signed URL in a new tab to trigger download
    window.open(exportItem.downloadUrl, '_blank');
    createToast({
      title: 'Downloading',
      description: `Downloading ${exportItem.fileName}...`,
      type: ToastTypes.Success,
      duration: 3000,
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Export Patient Charts</h2>
          {lastGeneratedTime && (
            <p className="text-sm text-gray-500 mb-4">
              Last export generated: <span className="font-medium">{lastGeneratedTime}</span>
            </p>
          )}

          <div className="mb-4">
            <span className="text-sm font-medium text-gray-700 block mb-2">
              Select data to export:
            </span>
            <div className="space-y-2 mb-4">
              {allSections.map(s => (
                <label key={s.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sections.includes(s.value)}
                    onChange={() => toggleSection(s.value)}
                    className="form-checkbox text-blue-600 mt-0.5"
                    disabled={generating}
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-700">{s.label}</span>
                    <span className="block text-xs text-gray-500">{s.description}</span>
                  </span>
                </label>
              ))}
            </div>

            {sections.includes('patient-charts') && (
              <div className="ml-6 mb-2">
                <span className="text-sm font-medium text-gray-700 mr-4">Chart format:</span>
                <label className="inline-flex items-center mr-4">
                  <input
                    type="radio"
                    name="exportFormat"
                    value="txt"
                    checked={format === 'txt'}
                    onChange={() => setFormat('txt')}
                    className="form-radio text-blue-600"
                    disabled={generating}
                  />
                  <span className="ml-1 text-sm text-gray-700">Text (TXT)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="exportFormat"
                    value="pdf"
                    checked={format === 'pdf'}
                    onChange={() => setFormat('pdf')}
                    className="form-radio text-blue-600"
                    disabled={generating}
                  />
                  <span className="ml-1 text-sm text-gray-700">PDF (signed encounters only)</span>
                </label>
              </div>
            )}
          </div>

          <button
            onClick={handleGenerateExport}
            disabled={generating}
            className={`px-6 py-2 rounded text-white font-medium transition-colors ${
              generating
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            {generating ? (
              <>
                <Loading />
                Generating Export...
              </>
            ) : (
              'Generate New Export'
            )}
          </button>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Available Exports</h3>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Loading exports...</p>
            </div>
          ) : exports.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded">
              <p className="text-gray-600">No exports available yet.</p>
              <p className="text-sm text-gray-500 mt-2">
                Click "Generate New Export" to create your first export.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Export Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {exports.map((exportItem, index) => (
                    <tr key={exportItem.key || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {exportItem.displayDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {exportItem.fileName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {exportItem.sizeFormatted}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDownload(exportItem)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {exports.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              <p>
                <strong>Note:</strong> Export files are available for download for 24 hours after
                the link is generated. Files are stored securely in S3 and can be re-accessed by
                refreshing this page.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicExports;
